import { useLocation } from '@tanstack/react-router'
import type * as React from 'react'
import { useEffect, useState } from 'react'

import {
  Card,
  CardContent,
  CardHeader,
  SpreadsheetBackground,
  TextField,
} from '@/components'
import { useCheckout } from '@/modules/account'
import {
  ACCOUNT_TYPES,
  AccountTypeToggle,
  ExistingUserFooter,
  FirmNameField,
  LegalFooter,
  OtpForm,
  PaymentTrust,
  type Plan,
  PlanInformation,
  PlansSelector,
  SubmitPlan,
  singleProjectPlan,
  TermsCheckbox,
  useAuthMutating,
  useAuthMutations,
  useFetchPlans,
  usePlans,
  ValueProps,
} from '@/modules/auth'
import { eventTracker } from '@/services/tracking'
import { useForm } from '@/stores'
import { cn } from '@/utils'

export const SignUpPage = () => {
  const location = useLocation()
  const searchParams = new URLSearchParams(location.searchStr)
  const { createAccountCheckout } = useCheckout()
  const [error, setError] = useState<string | null>(null)
  useFetchPlans()
  const plans = usePlans()

  const { signUp } = useAuthMutations()
  const isAuthMutating = useAuthMutating()
  const { data, errors, setFieldValue } = useForm()
  const [verifyCode, setVerifyCode] = useState(false)

  const selectedPriceId = data?.priceId as string | undefined
  const selectedPlan = plans.find((plan) => plan.priceId === selectedPriceId)

  const accountType = (data?.accountType as string) ?? ACCOUNT_TYPES.firm
  const isIndividual = accountType === ACCOUNT_TYPES.individual
  const accountName = isIndividual
    ? `${(data?.firstName as string) ?? ''} ${(data?.lastName as string) ?? ''}`.trim()
    : (data?.accountName as string)

  const sendCode = () =>
    signUp({
      email: data?.email as string,
      metadata: {
        firstName: data?.firstName as string,
        lastName: data?.lastName as string,
      },
    })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await sendCode()
      setVerifyCode(true)
    } catch (e) {
      const error = e as Error
      setError(error.message)
    }
  }

  const handleVerifiedCode = () => {
    eventTracker.signUp({
      email: data?.email as string,
      accountType,
      priceId: selectedPriceId,
    })

    if (selectedPriceId && !searchParams.get('priceId')) {
      searchParams.set('priceId', selectedPriceId)
    }
    if (accountName && !searchParams.get('name')) {
      searchParams.set('name', accountName)
    }

    createAccountCheckout({
      priceId: selectedPriceId as string,
      name: accountName,
      searchParams,
    })
  }

  useEffect(() => {
    const priceId = searchParams.get('priceId')
    if (priceId) {
      setFieldValue('priceId', priceId)
    }
  }, [searchParams.get('priceId')])

  useEffect(() => {
    if (!selectedPriceId && plans.length > 0) {
      setFieldValue('priceId', singleProjectPlan(plans)?.priceId)
    }
  }, [plans, selectedPriceId])

  return (
    <div className="min-h-screen bg-background px-4 sm:px-6 lg:px-8 py-8 sm:py-10 relative overflow-hidden">
      <SpreadsheetBackground />

      <div className="relative z-10 mx-auto w-full max-w-5xl xl:max-w-6xl">
        <div className="mb-8 flex flex-col items-center text-center">
          <img src="/logo.svg" alt="CounselPro" className="h-8 sm:h-9 w-auto" />
          <h1 className="mt-6 text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-balance">
            Turn weeks of financial analysis into minutes
          </h1>
          <p className="mt-3 max-w-2xl text-sm sm:text-base text-muted-foreground text-pretty">
            Pay a flat fee for a single case that you can bill straight to your
            client. Running analysis across many matters? Firm-wide yearly plans
            are available.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8 xl:gap-10 lg:items-start">
          <div className="lg:col-span-3 space-y-6">
            {selectedPlan && <PlanInformation plan={selectedPlan as Plan} />}

            <ValueProps className="hidden lg:grid pt-2" />
          </div>

          <Card className="lg:col-span-2 lg:sticky lg:top-8">
            <CardHeader className="space-y-1 pb-4">
              <h2 className="text-xl font-semibold tracking-tight text-center">
                Create your account
              </h2>
            </CardHeader>

            {verifyCode && (
              <OtpForm
                email={data?.email as string}
                onCodeVerified={handleVerifiedCode}
                onReturn={() => setVerifyCode(false)}
                onResend={sendCode}
              />
            )}

            <CardContent className={cn('px-4 sm:px-6', verifyCode && 'hidden')}>
              <form onSubmit={handleSubmit} className="space-y-4">
                <PlansSelector
                  value={data?.priceId as string}
                  error={errors?.priceId}
                  setFieldValue={setFieldValue}
                />
                <AccountTypeToggle
                  value={accountType}
                  setFieldValue={setFieldValue}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <TextField
                    label="First Name"
                    id="first-name"
                    placeholder="Type your first name"
                    error={errors?.firstName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue('firstName', e.target.value)
                    }
                    required
                  />

                  <TextField
                    label="Last Name"
                    id="last-name"
                    placeholder="Type your last name"
                    error={errors?.lastName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFieldValue('lastName', e.target.value)
                    }
                    required
                  />
                </div>

                {!isIndividual && (
                  <FirmNameField
                    error={errors?.accountName}
                    setFieldValue={setFieldValue}
                  />
                )}

                <TextField
                  label="Email Address"
                  id="email"
                  placeholder="Type your email address"
                  error={errors?.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFieldValue('email', e.target.value)
                  }
                  helpText="You will receive a 6-digit code to confirm your email address."
                  autoComplete="email"
                  required
                />

                <TermsCheckbox
                  value={data?.terms as boolean}
                  setFieldValue={setFieldValue}
                  isMutating={isAuthMutating}
                  className="pt-2"
                />

                <SubmitPlan
                  agreedToTerms={data?.terms as boolean}
                  isMutating={isAuthMutating}
                >
                  Sign up
                </SubmitPlan>
                {error && <p className="text-red-500 text-sm">{error}</p>}

                <PaymentTrust />
              </form>
            </CardContent>

            <ExistingUserFooter />
          </Card>
        </div>

        <LegalFooter />
      </div>
    </div>
  )
}
